/**
 * Eslesmeyen (iwasku IS NULL) kanal fiyat satirlari kuyrugu.
 * Her satir icin iwasku ata -> channel_sku_mappings'e yaz + yeniden coz.
 */

import React, { useEffect, useState } from 'react';
import { X, Loader2, Link2, CheckCircle } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { useModalA11y } from '../../hooks/useModalA11y';
import {
  apiGetChannelUnmatched,
  apiSaveChannelMapping,
  type UnmatchedRow,
} from '../../services/api';
import { logger } from '../../utils/logger';

interface Props {
  isOpen: boolean;
  country: string;
  onClose: () => void;
  onResolved: () => void;
}

const UnmatchedPanel: React.FC<Props> = ({ isOpen, country, onClose, onResolved }) => {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const modalRef = useModalA11y(isOpen, onClose);

  const [rows, setRows] = useState<UnmatchedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [done, setDone] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    apiGetChannelUnmatched(undefined, country)
      .then(setRows)
      .catch((e) => logger.error('unmatched load', e))
      .finally(() => setLoading(false));
  }, [isOpen, country]);

  if (!isOpen) return null;

  const assign = async (row: UnmatchedRow) => {
    const iwasku = (inputs[row.id] || '').trim();
    if (!iwasku) return;
    setSaving(row.id);
    try {
      await apiSaveChannelMapping(row.marketplace_sku, iwasku, row.channel_code);
      setDone((d) => ({ ...d, [row.id]: true }));
      onResolved();
    } catch (e) {
      logger.error('mapping save', e);
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[55] p-4">
      <div ref={modalRef} className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto" role="dialog" aria-modal="true">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="text-lg font-semibold text-slate-800">
            {tr ? 'Eşleşmeyen SKU\'lar' : 'Unmatched SKUs'} ({rows.length})
          </h3>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
              {tr ? 'Eşleşmeyen satır yok 🎉' : 'No unmatched rows 🎉'}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 mb-2">
                {tr
                  ? 'iwasku gir ve eşleştir → channel_sku_mappings\'e yazılır, ilgili satırlar otomatik çözülür.'
                  : 'Enter iwasku and map → saved to channel_sku_mappings, rows auto-resolved.'}
              </p>
              {rows.map((row) => (
                <div key={row.id} className={`flex items-center gap-2 p-2 rounded-lg border ${done[row.id] ? 'border-green-200 bg-green-50' : 'border-slate-200'}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm text-slate-800 truncate">{row.marketplace_sku}</div>
                    <div className="text-xs text-slate-400">{row.channel_code} · {row.price ?? '—'}</div>
                  </div>
                  {done[row.id] ? (
                    <span className="text-green-600 text-sm flex items-center gap-1"><CheckCircle className="w-4 h-4" /> {tr ? 'eşleşti' : 'mapped'}</span>
                  ) : (
                    <>
                      <input
                        type="text"
                        placeholder="iwasku"
                        value={inputs[row.id] || ''}
                        onChange={(e) => setInputs((s) => ({ ...s, [row.id]: e.target.value }))}
                        className="w-44 px-2 py-1.5 text-sm font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      />
                      <button
                        onClick={() => assign(row)}
                        disabled={saving === row.id || !(inputs[row.id] || '').trim()}
                        className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-sm flex items-center gap-1"
                      >
                        {saving === row.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                        {tr ? 'Eşleştir' : 'Map'}
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnmatchedPanel;
